import React, { useState } from 'react';
import {
  IonIcon,
  IonText,
  IonItem,
  IonLabel,
  IonButton,
  IonPopover,
  IonList,
  IonCheckbox,
  IonBadge
} from '@ionic/react';
import {
  thumbsUpOutline,
  thumbsUp,
  ellipsisVertical,
  eyeOffOutline,
  checkmarkCircleOutline,
  checkmarkCircle,
  eyeOff
} from 'ionicons/icons';
import './QuestionCard.css';
import { Question } from '../pages/SessionDetail';

interface QuestionCardProps {
  question: Question;
  onLike: (questionId: string, likesCount: number) => void;
  onHide?: (questionId: string) => void;
  onMarkAsAnswered?: (questionId: string, isAnswered: boolean) => void;
  isHidden?: boolean;
  isAnswered?: boolean;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  onLike,
  onHide,
  onMarkAsAnswered,
  isHidden = false,
  isAnswered = false
}) => {
  const [showPopover, setShowPopover] = useState(false);

  const handleHide = () => {
    if (onHide) {
      onHide(question.id!);
    }
    setShowPopover(false);
  };

  const handleMarkAsAnswered = () => {
    if (onMarkAsAnswered) {
      onMarkAsAnswered(question.id!, !isAnswered);
    }
    setShowPopover(false);
  };

  return (
    <IonItem
      className={`question-card rounded my-2 shadow-sm hover-up transition-fast ${isHidden ? 'question-hidden' : ''} ${isAnswered ? 'question-answered' : ''}`}
      lines="none"
    >
      <IonLabel style={{ whiteSpace: 'normal', color: 'var(--neutral-900)' }} className="ion-padding-vertical">
        <div className="question-header">
          <h3 style={{ fontWeight: '600', color: 'var(--neutral-900)', marginBottom: '8px', flex: 1 }}>
            {question.title}
          </h3>

          {/* Badges de estado */}
          <div className="question-status-badges">
            {isAnswered && (
              <IonBadge color="success" className="status-badge">
                <IonIcon icon={checkmarkCircle} />
                Respondida
              </IonBadge>
            )}
            {isHidden && (
              <IonBadge color="medium" className="status-badge">
                <IonIcon icon={eyeOff} />
                Oculta
              </IonBadge>
            )}
          </div>

          {/* Botón de menú contextual - solo para usuarios con permisos */}
          {(onHide || onMarkAsAnswered) && (
            <IonButton
              fill="clear"
              size="small"
              id={`question-menu-${question.id}`}
              className="question-menu-button"
              onClick={() => setShowPopover(true)}
            >
              <IonIcon icon={ellipsisVertical} />
            </IonButton>
          )}
        </div>

        <div className="question-footer">
          <div
            className={`like-button ${question.yaDioLike ? 'liked' : ''}`}
            onClick={() => !question.yaDioLike && onLike(question.id!, question.likesCount)}
          >
            <IonIcon icon={question.yaDioLike ? thumbsUp : thumbsUpOutline} />
            <span>{question.likesCount}</span>
          </div>
        </div>
      </IonLabel>

      {/* Popover del menú contextual - solo para usuarios con permisos */}
      {(onHide || onMarkAsAnswered) && (
        <IonPopover
          trigger={`question-menu-${question.id}`}
          isOpen={showPopover}
          onDidDismiss={() => setShowPopover(false)}
          showBackdrop={true}
        >
          <IonList>
            {onMarkAsAnswered && (
              <IonItem button onClick={handleMarkAsAnswered}>
                <IonIcon
                  icon={isAnswered ? checkmarkCircle : checkmarkCircleOutline}
                  slot="start"
                  color={isAnswered ? "success" : "medium"}
                />
                <IonLabel>
                  {isAnswered ? 'Marcar como no respondida' : 'Marcar como respondida'}
                </IonLabel>
              </IonItem>
            )}

            {onHide && (
              <IonItem button onClick={handleHide}>
                <IonIcon
                  icon={eyeOffOutline}
                  slot="start"
                  color="medium"
                />
                <IonLabel>
                  {isHidden ? 'Mostrar pregunta' : 'Ocultar pregunta'}
                </IonLabel>
              </IonItem>
            )}
          </IonList>
        </IonPopover>
      )}
    </IonItem>
  );
};

export default QuestionCard;
